/**
 * Pure phase request mappers (#2239). Kept free of React/API-client imports
 * so the contract is browserless-testable.
 *
 * Every field is assigned individually so the compiler guards schema drift —
 * no blanket casts. Enum values (phase type, metric type) are guarded at
 * runtime: unknown values throw loudly instead of being smuggled to the API.
 *
 * Phase DELETE exists in the contract and is intentionally NOT exposed as a
 * hook: patient flows edit phases via PATCH (steps are cleared by clearing
 * their data) and cycles are deleted wholesale. Deleting a single phase
 * would silently drop its verified sub-records (trigger shot, metrics,
 * spermiograms). Revisit only with an explicit product decision.
 */

import type { paths } from "@src/packages/openapi/schema";
import type { PhaseEmbryoDataWrite } from "@src/packages/patients/embryoGrades";
import type { PhaseEmbryoTransferInfoBody } from "@src/packages/patients/embryoTransferInfo";

type CreatePhaseBody = NonNullable<
  paths["/{tenantId}/cycles/{id}/phases"]["post"]["requestBody"]
>["content"]["application/json"];
type UpdatePhaseBody = NonNullable<
  paths["/{tenantId}/cycles/{id}/phases/{phaseId}"]["patch"]["requestBody"]
>["content"]["application/json"];

export type PhaseSpermiogramInput = NonNullable<
  CreatePhaseBody["spermiograms"]
>[number];
export type PhaseProcedureInput = Exclude<
  NonNullable<CreatePhaseBody["procedures"]>[number],
  { op: "delete" }
>;
export type PhaseInseminationInfoInput = NonNullable<
  CreatePhaseBody["inseminationInfo"]
>;
export type PhaseTestResult = NonNullable<
  UpdatePhaseBody["testResults"]
>[number];

export type PhaseSubObjects = {
  triggerShot?: {
    id?: string;
    date?: string | null;
    time?: string | null;
    notes?: string | null;
  } | null;
  spermiograms?: {
    id?: string;
    patientId: string;
    semenSpecimenId?: string | null;
    origin?: "partner" | "donor" | null;
    spermType?: "fresh" | "frozen" | null;
    technique?: PhaseSpermiogramInput["technique"];
    preparationMethod?: PhaseSpermiogramInput["preparationMethod"];
    /**
     * Who the row's content came from. Stated on a patient write rather than
     * left to a default, so a figure she typed is never presented as the
     * laboratory's — see `buildAnalysisSpermiogram`.
     */
    source?: PhaseSpermiogramInput["source"];
    date?: string | null;
    time?: string | null;
    // The semen-analysis figures. Each has its own column on `spermiograms`,
    // which is what the canonical spermiogram surface returns and what a clinic
    // reads — see `addAnalysisSpermiogramFields`.
    spermConcentration?: number | null;
    spermCount?: number | null;
    semenVolume?: number | null;
    volumeUnit?: string | null;
    motility?: number | null;
    progressiveMotility?: number | null;
    normalMorphology?: number | null;
    notes?: string | null;
  }[];
  procedures?: PhaseProcedureInput[];
  inseminationInfo?: {
    id?: string;
    date?: string | null;
    time?: string | null;
    inseminationType?: PhaseInseminationInfoInput["inseminationType"];
    catheterType?: PhaseInseminationInfoInput["catheterType"];
    catheterBrand?: string | null;
    ultrasoundGuided?: boolean | null;
    technique?: PhaseInseminationInfoInput["technique"];
    inseminationDuration?: number | null;
    notes?: string | null;
  };
  eggRetrievalInfo?: { id?: string; notes?: string | null };
  embryoTransferInfo?: PhaseEmbryoTransferInfoBody;
  embryoData?: PhaseEmbryoDataWrite;
  testResults?: PhaseTestResult[];
};

const PHASE_TYPES = [
  "protocol",
  "egg_retrieval",
  "fertilization",
  "sperm_collection",
  "insemination",
  "embryo_development",
  "transfer",
  "luteal_phase",
  "pre_treatment",
] as const;
type PhaseType = (typeof PHASE_TYPES)[number];

function assertPhaseType(value: string): PhaseType {
  if ((PHASE_TYPES as readonly string[]).includes(value)) {
    return value as PhaseType;
  }
  throw new Error(`Unknown phase type "${value}": not in the API contract`);
}

/**
 * Metric types the phase contract accepts — mirrors the generated enum so
 * unknown metric keys fail loudly at the mapper instead of being smuggled
 * past the compiler and silently rejected server-side.
 */
type PhaseMetricInput = Exclude<
  NonNullable<UpdatePhaseBody["fertilityMetrics"]>[number],
  { op: "delete" }
>;
/** Every metric type the generated phase contract accepts. */
type SchemaMetricType = NonNullable<PhaseMetricInput["type"]>;

export const PHASE_METRIC_TYPES = [
  "follicles_over_17_mm",
  "m2_count",
  "motility_deprecated",
  "transferred_embryo",
  "frozen_embryo",
  "retrieved_oocytes",
  "injected_oocytes",
  "fertilized_oocytes",
  "estrogen_min",
  "estrogen_max",
  "tmsc_deprecated",
  "endometrial_thickness",
  "embryos_day_3",
  "embryos_day_5",
  "immature_oocytes",
  "degenerated_oocytes",
] as const satisfies readonly SchemaMetricType[];
type PhaseMetricType = (typeof PHASE_METRIC_TYPES)[number];

/**
 * "Mirrors the generated enum" was a promise this list could not keep on its
 * own, and it had already stopped keeping it: `injected_oocytes` was in the
 * contract and in `STEP_FIELD_TO_METRIC`, absent here, so every save carrying
 * an injected-oocyte count threw the mapper's own error — and the three
 * `catch` blocks around the specimen intake's post-save discarded the message,
 * leaving only "the treatment step could not be updated".
 *
 * The `satisfies` above holds one direction: nothing here can be a value the
 * contract does not have. The other direction — a value the contract has and
 * this list lacks — is asserted by `phase-request-mappers.test.ts`, which
 * iterates `STEP_FIELD_TO_METRIC`, because that is the direction that bit.
 *
 * Deliberately NOT a compile-time assertion. `schema.d.ts` is regenerated from
 * the *live* dev API on every typecheck, so an `Exclude<…> extends never`
 * guard here would turn any additive backend enum change into a repo-wide
 * build break — every open PR failing typecheck, deploys held, including PRs
 * that never touch phases. A failing test names the missing value just as
 * loudly without taking unrelated work down with it.
 */

function isPhaseMetricType(value: string): value is PhaseMetricType {
  return (PHASE_METRIC_TYPES as readonly string[]).includes(value);
}

/**
 * A write against a phase's metric rows, in the contract's own two shapes: a
 * row carrying a value, or a removal carrying nothing but its id.
 *
 * The removal exists because a null value is not the same as no row. A row that
 * exists with a null value reads back as "I don't know" — which is exactly what
 * `clearedOocyteCounts` looks for — so withdrawing that answer has to take the
 * row away, not blank it.
 */
export type PhaseMetricWrite =
  | { id?: string; type: string; value: number | null }
  | { id: string; op: "delete" };

function mapPhaseMetrics(
  metrics: PhaseMetricWrite[] | undefined,
):
  | (
      | { id?: string; type: PhaseMetricType; value: number | null }
      | { id: string; op: "delete" }
    )[]
  | undefined {
  if (!metrics) return undefined;
  return metrics.map((metric) => {
    // A removal names a row; it says nothing about the row's type, so there is
    // no type to validate.
    if ("op" in metric) return metric;
    if (!isPhaseMetricType(metric.type)) {
      throw new Error(
        `Unknown phase metric type "${metric.type}": not in the API contract`,
      );
    }
    return { ...metric, type: metric.type };
  });
}

export type CreatePhaseInput = {
  type: string;
  startDate: string;
  startTime?: string | null;
  endDate?: string | null;
  fertilityMetrics?: { type: string; value: number | null }[];
} & PhaseSubObjects;

export type UpdatePhaseInput = {
  startDate?: string | null;
  startTime?: string | null;
  endDate?: string | null;
  fertilityMetrics?: PhaseMetricWrite[];
} & PhaseSubObjects;

function mapSubObjects(body: PhaseSubObjects) {
  return {
    triggerShot: body.triggerShot,
    spermiograms: body.spermiograms,
    procedures: body.procedures,
    inseminationInfo: body.inseminationInfo,
    eggRetrievalInfo: body.eggRetrievalInfo,
    embryoTransferInfo: body.embryoTransferInfo,
    embryoData: body.embryoData,
    testResults: body.testResults,
  };
}

export function toCreatePhaseBody(body: CreatePhaseInput): CreatePhaseBody {
  return {
    type: assertPhaseType(body.type),
    startDate: body.startDate,
    startTime: body.startTime,
    endDate: body.endDate,
    fertilityMetrics: mapPhaseMetrics(body.fertilityMetrics),
    ...mapSubObjects(body),
  };
}

export function toUpdatePhaseBody(body: UpdatePhaseInput): UpdatePhaseBody {
  return {
    startDate: body.startDate,
    startTime: body.startTime,
    endDate: body.endDate,
    fertilityMetrics: mapPhaseMetrics(body.fertilityMetrics),
    ...mapSubObjects(body),
  };
}
